import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Circle, MoveUpRight, Pencil, RotateCcw, Square, Trash2, X } from 'lucide-react';

/**
 * Anotador del pantallazo del botón Reportar: lápiz, flecha, círculo y
 * rectángulo en rojo, con deshacer. Canvas API pura (sin librerías de dibujo:
 * pesarían más que todo el módulo) y pointer events — el mismo código sirve
 * para dedo, stylus y ratón, que en tablet es donde vive el caso real
 * ("rodear el botón que falla"). Al guardar se aplana a PNG y sustituye a la
 * captura: el backend recibe el mismo adjunto de siempre.
 */

const COLOR = '#ef4444'; // rojo de anotación de bugs; esto no es un editor de fotos

const HERRAMIENTAS = [
  { id: 'lapiz', icono: Pencil, label: 'Lápiz' },
  { id: 'flecha', icono: MoveUpRight, label: 'Flecha' },
  { id: 'circulo', icono: Circle, label: 'Círculo' },
  { id: 'rect', icono: Square, label: 'Rectángulo' },
];

function dibujarForma(ctx, forma, grosor) {
  ctx.strokeStyle = COLOR;
  ctx.lineWidth = grosor;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  if (forma.tool === 'lapiz') {
    forma.puntos.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  } else if (forma.tool === 'flecha') {
    const [x1, y1] = forma.desde;
    const [x2, y2] = forma.hasta;
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    // Punta: dos trazos cortos a ±30° del eje de la línea.
    const angulo = Math.atan2(y2 - y1, x2 - x1);
    const largo = Math.max(grosor * 3.5, 14);
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - largo * Math.cos(angulo - Math.PI / 6), y2 - largo * Math.sin(angulo - Math.PI / 6));
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - largo * Math.cos(angulo + Math.PI / 6), y2 - largo * Math.sin(angulo + Math.PI / 6));
  } else if (forma.tool === 'circulo') {
    const [x1, y1] = forma.desde;
    const [x2, y2] = forma.hasta;
    ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
  } else if (forma.tool === 'rect') {
    const [x1, y1] = forma.desde;
    const [x2, y2] = forma.hasta;
    ctx.rect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
  }
  ctx.stroke();
}

export default function CaptureAnnotator({ dataUrl, onGuardar, onCancelar }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const formasRef = useRef([]);
  const dibujandoRef = useRef(false);
  const [tool, setTool] = useState('lapiz');
  const [nFormas, setNFormas] = useState(0); // solo para habilitar/deshabilitar Deshacer
  const [lista, setLista] = useState(false);
  const [falloCarga, setFalloCarga] = useState(false);

  // Grosor relativo a la resolución de la captura (html2canvas escala por DPR):
  // fijo en px se vería finísimo en tablets retina.
  const grosor = () => Math.max(4, Math.round((canvasRef.current?.width || 800) / 240));

  const redibujar = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (!canvas || !img || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const g = grosor();
    formasRef.current.forEach((forma) => dibujarForma(ctx, forma, g));
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setLista(true);
      redibujar();
    };
    // Sin onerror, una dataURL que no decodifica dejaba el anotador clavado en
    // «Preparando el pantallazo…» con Guardar desactivado y sin salida.
    img.onerror = () => setFalloCarga(true);
    img.src = dataUrl;
  }, [dataUrl, redibujar]);

  const coords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) * canvas.width) / rect.width,
      ((e.clientY - rect.top) * canvas.height) / rect.height,
    ];
  };

  const onPointerDown = (e) => {
    if (!lista) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dibujandoRef.current = true;
    const punto = coords(e);
    formasRef.current.push(
      tool === 'lapiz' ? { tool, puntos: [punto] } : { tool, desde: punto, hasta: punto }
    );
    setNFormas(formasRef.current.length);
  };

  const onPointerMove = (e) => {
    if (!dibujandoRef.current) return;
    e.preventDefault();
    const forma = formasRef.current[formasRef.current.length - 1];
    const punto = coords(e);
    if (forma.tool === 'lapiz') forma.puntos.push(punto);
    else forma.hasta = punto;
    redibujar();
  };

  const onPointerUp = () => {
    dibujandoRef.current = false;
  };

  const deshacer = () => {
    formasRef.current.pop();
    setNFormas(formasRef.current.length);
    redibujar();
  };

  const borrarTodo = () => {
    formasRef.current = [];
    setNFormas(0);
    redibujar();
  };

  const guardar = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    redibujar(); // por si el último trazo quedó a medio pintar
    onGuardar(canvas.toDataURL('image/png'));
  };

  return (
    <div
      data-smack-report="true"
      className="fixed inset-0 z-[100030] flex flex-col bg-black/70 p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Anotar pantallazo"
    >
      {/* Barra de herramientas */}
      <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-1 rounded-xl bg-white/95 p-1">
          {HERRAMIENTAS.map(({ id, icono: Icono, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTool(id)}
              aria-label={label}
              aria-pressed={tool === id}
              className={`rounded-lg p-2 ${
                tool === id ? 'bg-[#FF5400] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icono size={16} />
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
          <button
            type="button"
            onClick={deshacer}
            disabled={nFormas === 0}
            aria-label="Deshacer"
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            onClick={borrarTodo}
            disabled={nFormas === 0}
            aria-label="Borrar todas las anotaciones"
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
          >
            <Trash2 size={16} />
          </button>
        </div>
        <div className="flex items-center gap-1 rounded-xl bg-white/95 p-1">
          <button
            type="button"
            onClick={onCancelar}
            aria-label="Cancelar anotación"
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={!lista}
            aria-label="Guardar anotación"
            className="rounded-lg bg-[#FF5400] p-2 text-white hover:opacity-90 disabled:opacity-40"
          >
            <Check size={16} />
          </button>
        </div>
      </div>

      {/* Lienzo */}
      <div className="flex min-h-0 flex-1 items-center justify-center">
        {!lista && !falloCarga && (
          <p className="text-sm text-white/80">Preparando el pantallazo…</p>
        )}
        {falloCarga && (
          <p className="text-sm text-red-300">
            No se pudo abrir el pantallazo para anotarlo. Cierra y envía el reporte sin anotar.
          </p>
        )}
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className={`max-h-full max-w-full rounded-lg border border-white/20 bg-white ${
            lista ? '' : 'hidden'
          }`}
          style={{ touchAction: 'none', cursor: 'crosshair' }}
        />
      </div>
    </div>
  );
}
